import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { API } from "@/contexts/AuthContext";

const PartsPackList = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/foh/parts-packs`)
      .then(res => setPacks(res.data))
      .catch(err => console.error("Failed to load parts packs:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Parts Packs</h1>
            <p className="text-slate-500">{packs.length} active packs</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/app/foh")}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Dashboard
          </Button>
        </div>

        {packs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No Parts Packs yet</p>
              <p className="text-sm mt-2">Parts Packs are generated after customer authorisation</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {packs.map(pack => (
              <Card 
                key={pack.id} 
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/app/foh/parts-pack/${pack.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{pack.reg}</span>
                        <span className="text-slate-500">{pack.make} {pack.model}</span>
                      </div>
                      {pack.customer_name && (
                        <p className="text-sm text-slate-500">{pack.customer_name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-sm">
                        <div className="flex gap-2">
                          {pack.summary?.not_ordered > 0 && (
                            <Badge className="bg-gray-200 text-gray-700">{pack.summary.not_ordered}</Badge>
                          )}
                          {pack.summary?.ordered > 0 && (
                            <Badge className="bg-blue-200 text-blue-700">{pack.summary.ordered}</Badge>
                          )}
                          {pack.summary?.received > 0 && (
                            <Badge className="bg-amber-200 text-amber-700">{pack.summary.received}</Badge>
                          )}
                          {pack.summary?.fitted > 0 && (
                            <Badge className="bg-green-200 text-green-700">{pack.summary.fitted}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{pack.job_count} jobs</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartsPackList;
